import React, { useState } from 'react';
import { Form, Input, Checkbox, Icon, Popup, Button } from 'semantic-ui-react';
import { NumericFormat, NumberFormatValues } from 'react-number-format';

type OperatingLoanValues = {
    id: string;
    loanName?: string | null;
    amount?: number | null;
    rate?: number | null;
    term?: number | null;
    amortization?: number | null;
    availableForConstruction?: boolean;
};

type OperatingLoansValues = {
    loans: OperatingLoanValues[];
};

type Props = {
    values?: OperatingLoansValues;
    onChange: (values: OperatingLoansValues) => void;
};

const createDefaultLoan = (): OperatingLoanValues => ({
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    loanName: null,
    amount: null,
    rate: null,
    term: null,
    amortization: null,
    availableForConstruction: false,
});

const OperatingLoans: React.FC<Props> = ({
    values = { loans: [] },
    onChange,
}) => {
    const [formValues, setFormValues] = useState<OperatingLoansValues>({
        loans: values.loans.length > 0 ? values.loans : [],
    });

    const addLoan = () => {
        if (formValues.loans.length >= 2) return; // Limit to 2 loans
        const newLoan = createDefaultLoan();
        const updatedValues = {
            loans: [...formValues.loans, newLoan]
        };
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    const deleteLoan = (loanId: string) => {
        const updatedValues = {
            loans: formValues.loans.filter(loan => loan.id !== loanId)
        };
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    const handleTextChange = (
        loanId: string,
        name: string,
        value: any
    ) => {
        let newValue: string | number | null;
        if (name === 'loanName') {
            newValue = value === '' ? null : value;
        } else {
            newValue = value === '' ? null : Number(value);
        }
        const updatedValues = {
            loans: formValues.loans.map(loan =>
                loan.id === loanId
                    ? { ...loan, [name]: newValue }
                    : loan
            )
        };
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    const handleCheckboxChange = (
        loanId: string,
        _e: React.FormEvent<HTMLInputElement>,
        data: { name?: string; checked?: boolean }
    ) => {
        const { name, checked } = data;
        if (name === undefined) return;
        const updatedValues = {
            loans: formValues.loans.map(loan =>
                loan.id === loanId
                    ? { ...loan, [name]: checked }
                    : loan
            )
        };
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    const renderLoanFields = (loan: OperatingLoanValues, index: number) => (
        <div key={loan.id} style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <h3>Operating Loan {index + 1}</h3>

            <Form.Field>
                <label>Operating loan amount</label>
                <NumericFormat
                    thousandSeparator
                    allowNegative={false}
                    customInput={Input}
                    name="amount"
                    value={loan.amount ?? ''}
                    onValueChange={values =>
                        handleTextChange(loan.id, 'amount', values.value)
                    }
                    placeholder="Enter amount..."
                    label={{ basic: true, content: '$' }}
                    labelPosition="right"
                />
            </Form.Field>
            <Form.Field>
                <label>Operating loan annual rate</label>
                <NumericFormat
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    customInput={Input}
                    name="rate"
                    value={loan.rate ?? ''}
                    onValueChange={values =>
                        handleTextChange(loan.id, 'rate', values.value)
                    }
                    placeholder="Enter rate..."
                    label={{ basic: true, content: '%' }}
                    labelPosition="right"
                />
            </Form.Field>
            <Form.Field>
                <label>Operating loan term</label>
                <NumericFormat
                    allowNegative={false}
                    customInput={Input}
                    name="term"
                    value={loan.term ?? ''}
                    onValueChange={values =>
                        handleTextChange(loan.id, 'term', values.value)
                    }
                    placeholder="Enter term..."
                    label={{ basic: true, content: 'years' }}
                    labelPosition="right"
                    decimalScale={0}
                    min={0}
                />
            </Form.Field>
            <Form.Field>
                <label>Operating loan amortization period</label>
                <NumericFormat
                    allowNegative={false}
                    customInput={Input}
                    name="amortization"
                    value={loan.amortization ?? ''}
                    onValueChange={values =>
                        handleTextChange(loan.id, 'amortization', values.value)
                    }
                    placeholder="Enter amortization..."
                    label={{ basic: true, content: 'years' }}
                    labelPosition="right"
                    decimalScale={0}
                    min={0}
                />
            </Form.Field>
            <Form.Field>
                <label>Available for construction?</label>
                <Checkbox
                    key={loan.id + '_availableForConstruction'}
                    name="availableForConstruction"
                    checked={loan.availableForConstruction === true}
                    toggle
                    onChange={(e, data) => handleCheckboxChange(loan.id, e, data)}
                    label={loan.availableForConstruction ? 'Yes' : 'No'}
                />
            </Form.Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #ddd', paddingTop: '16px' }}>
                <Button
                    basic
                    color="red"
                    size="small"
                    onClick={() => deleteLoan(loan.id)}
                    title="Delete loan"
                >Remove</Button>
            </div>
        </div>
    );

    return (
        <Form>
            <h4>Operating Loans</h4>

            {formValues.loans.map((loan, index) => renderLoanFields(loan, index))}

            {formValues.loans.length < 2 && (
                <Button
                    secondary
                    type="button"
                    icon="plus"
                    content="Add operating loan"
                    fluid
                    onClick={addLoan}
                    style={{ marginBottom: '24px', padding: '16px', textAlign: 'center', boxShadow: 'none' }}
                />
            )}
        </Form>
    );
};

export default OperatingLoans;
